import fetch from "cross-fetch";
import { ApolloClient, split, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
// import { persistCache, AsyncStorageWrapper } from "apollo3-cache-persist";
import { RetryLink } from "@apollo/client/link/retry";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { setContext } from "@apollo/client/link/context";
import { GraphQLRequest } from "@apollo/client/link/core";
// import FSStorage from 'redux-persist-fs-storage';
import { WebSocketLink } from "@apollo/client/link/ws";
// import firebase from 'react-native-firebase';

// schema
// import possibleTypes from "@schema/possibleTypes.json";

// state
// import { ANALYTICS_USER, jwt, xsrf } from "@state/storage";
import { jwt, xsrf } from "@state/local-storage";

// constants
import { API_URL } from "@clockwise/client-commons/src/constants/api";
import { clientId } from "@clockwise/web-commons/src/util/fetch.util";
import { genTxnId } from "@clockwise/client-commons/src/util/txn";

// util
// import {
//   Environment,
//   getGraphUrlForEnvironment,
//   getGraphUrlForStoredEnvironment,
//   getBatchUrlForEnvironment,
//   getSubscriptionUrlForStoredEnvironment,
// } from "./environment";
import { getSubscriptionUrlForStoredEnvironment } from "@network/environment";
// import { doLogout } from "@util/logout";
// import { dataIdFromObject } from "@util/data";
// import { fromGlobalId, toGlobalId } from "@clockwise/client-commons/src/util/graphql";
// import { UrnUtil } from "@clockwise/client-commons/src/util/urn";

const graphUrl = `${API_URL}/graphql`;
const graphUrlBatch = `${graphUrl}/batch`;

/**
 * This sets the url we're talking to, and the jwt if we have one.
 */

const formNetworkLink = (graphUrl: string) => async (_: GraphQLRequest, prevContext: any) => {
  const _jwt = await jwt.get();
  const Authorization = _jwt && `Bearer ${_jwt}`;
  const Xsrf = await xsrf.get();

  const headers = { ...prevContext.previousHeaders };
  if (Authorization) {
    headers.Authorization = Authorization;
  }

  if (Xsrf) {
    headers.Xsrf = Xsrf;
  }

  const txnId = genTxnId();

  headers.txnId = txnId;
  headers.clientId = clientId;

  return {
    graphUrl,
    headers,
  };
};

const clockwiseNetworkLink = setContext(formNetworkLink(graphUrl));

const clockwiseBatchNetworkLink = setContext(formNetworkLink(graphUrlBatch));

// const monitoringFetch: typeof fetch = async (input: RequestInfo, init?: RequestInit) => {
// const metric = await firebase.perf().newHttpMetric(input as string, init!.method as any);
// await metric.start();
// const response = await fetch(input, init);
// await metric.setHttpResponseCode(response.status);
// await metric.setResponseContentType(response.headers.get('Content-Type')!);
// await metric.setResponseContentType(response.headers.get('Content-Length')!);
// const json = await response.clone().json();
// await metric.putAttribute('cached', json.cached ? 'true' : 'false');
// await metric.stop();
// return response;
// };

/**
 * This url gets overriden by the clockwise network link.
 */
const httpLink = new HttpLink({
  uri: `${API_URL}/graphql`,
  credentials: "include",
  fetch, // Provide a cross environment version of fetch, so that this can run in both the browser and tests.
});

const batchHttpLink = new BatchHttpLink({
  uri: `${API_URL}/graphql/batch`,
  credentials: "include",
  batchInterval: 50,
  fetch, // Provide a cross environment version of fetch, so that this can run in both the browser and tests.
});

// All queries sent batched, all mutation sent non batched.
const combinedBatchLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === "OperationDefinition" && definition.operation !== "mutation";
  },
  ApolloLink.from([clockwiseBatchNetworkLink, batchHttpLink]),
  ApolloLink.from([clockwiseNetworkLink, httpLink]),
);

// See https://www.apollographql.com/docs/react/api/link/apollo-link-retry/ for RetryLink's defaults.
const retryLink = new RetryLink({
  attempts: { max: 5 }, // This is the default, and is done with jitter true to add some randomness
  // and an exponential delay increase starting from 30 ms and doubling.
});

/**
 * Need to let the cache know about our union types.
 * See https://www.apollographql.com/docs/react/advanced/fragments.html#fragment-matcher.
 */
export const cache = new InMemoryCache({
  // dataIdFromObject,
  // possibleTypes,
  typePolicies: {
    Viewer: {
      fields: {
        schedulingLinkDetails: {
          // https://github.com/apollographql/apollo-client/issues/7314 Explains the syntax
          // for using nested fields as part of keyArgs. [field1, field2, fieldWithNested, [nestedField1, ...]]
          keyArgs: ["linkName", "slug", "body", ["timeZone"]],
        },
      },
    },
    PublicBooking: {
      fields: {
        schedulingLinkDetails: {
          keyArgs: ["timeZone"],
        },
      },
    },
    SchedulingLinkDetails: {
      merge: true,
      fields: {
        suggestedSlotsByDate: {
          merge(existing: any[], incoming: any[], { readField }) {
            // This is basically a reducer for concatenating new suggestedSlotsByDate into
            // matching SchedulingLinkDetails object.
            const newSlotsByDate: any[] = existing ? existing.slice(0) : [];
            const byDateMap: Record<string, number> = Object.create(null);
            if (existing) {
              existing.forEach((slotsByDate, index) => {
                const date = readField<string>("date", slotsByDate);
                if (date) {
                  // Needed just because readField can technically return undefined. Should be defined.
                  byDateMap[date] = index;
                }
              });
            }
            // For each new object, if we already have that date replace the previous times,
            // else concatenate onto existing suggestedSlotsByDate.
            incoming.forEach((slotsByDate) => {
              const date = readField<string>("date", slotsByDate);
              if (!date) {
                return; // Needed just because readField can technically return undefined. Should be defined.
              }
              const index = byDateMap[date];
              if (typeof index === "number") {
                // Replace existing slots with new possibily updated slots for that date.
                newSlotsByDate[index] = slotsByDate;
              } else {
                newSlotsByDate.push(slotsByDate);
              }
            });

            // Sort by date.
            newSlotsByDate.sort((a, b) =>
              (readField<string>("date", a) || "") < (readField<string>("date", b) || "") ? -1 : 1,
            );
            return newSlotsByDate;
          },
        },
      },
    },
    // SchedulingLinkDetails: {
    //   // Want to achieve:
    //   // Fetch slots for a timeRange. Fetch a new timerange
    //   merge: true,
    //   fields: {
    //     schedulingLinkDetails: {
    //       keyArgs: (args) =>
    //         `${args?.["slug"]}${args?.["linkName"]}${args?.["body"]?.["timeZone"]}`,
    //     },
    //     suggestedSlots: {

    //     },
    //   },
    // },
  },
});

// export const persistor = new CachePersistor({
//   cache,
//   trigger: 'background',
//   // see https://github.com/apollographql/apollo-cache-persist/issues/75
//   storage: FSStorage(),
// });

const subscriptionUrl = getSubscriptionUrlForStoredEnvironment();

const wsLink = new WebSocketLink({
  uri: subscriptionUrl,
  options: {
    reconnect: true,
    lazy: true,
    connectionParams: () =>
      Promise.all([jwt.get(), xsrf.get()]).then(([jwt, xsrf]) => {
        return {
          Authorization: jwt && `Bearer ${jwt}`,
          Xsrf: xsrf,
        };
      }),
  },
});

const combinedWSLink = split(
  // split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === "OperationDefinition" && definition.operation === "subscription";
  },
  ApolloLink.from([clockwiseNetworkLink, wsLink]),
  ApolloLink.from([combinedBatchLink]),
);

const omitTypenameLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = JSON.parse(JSON.stringify(operation.variables), (key, value) => {
      return key === "__typename" ? undefined : value;
    });
  }

  if (!forward) {
    return null;
  }

  return forward(operation);
});

// our custom "afterware" that checks each response and sets jwt/xsrf from header
const afterwareLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const context = operation.getContext();
    const { headers } = context.response;

    // maybe set the jwt
    // @ts-ignore
    const jwtToken = headers.get("jwt");
    if (jwtToken && jwtToken !== jwt.get()) {
      jwt.set(jwtToken);
    }

    // maybe set the xsrf
    // @ts-ignore
    const xsrfToken = headers.get("xsrf");
    if (xsrfToken && xsrfToken !== xsrf.get()) {
      xsrf.set(xsrfToken);
    }

    return response;
  });
});

export const client = new ApolloClient({
  cache,
  connectToDevTools: process.env.NODE_ENV !== "production",
  link: ApolloLink.from([afterwareLink, omitTypenameLink, retryLink, combinedWSLink]),
});
